import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { Row, Col, Table, Tabs, Button, Divider, Typography, notification, Tooltip } from "antd";
import SurveysInfoForm from "./SurveysInfoForm";
import SurveysDamageInfo from "./SurveysDamageInfo";
import axios from "axios";
import "./Survey.css";
import dayjs from 'dayjs';
import { AppleOutlined, AndroidOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

const Surveys = props => {
  const { t } = useTranslation();
  const [currentPageSurveys, setCurrentPageSurveys] = useState([]);
  const [listState, setListState] = useState({
    pagination: { pageSize: 10, current: 1 },
    filters: {},
    sorter: {}
  });
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [editState, setEditState] = useState({ survey: {id:-1, enterpriseId: -1, eventId: -1}, valid: false });
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("1"); // "1" is the key for the Info tab

  const handleTableChange = (pagination, filters, sorter) => {
    // Convert antd's sorter object to a format we can use
    const sortConfig = sorter.field ? {
      field: sorter.field,
      order: sorter.order // 'ascend' or 'descend'
    } : {};

    setListState({
      pagination: pagination,
      filters: filters,
      sorter: sortConfig
    });
  };

  const saveSurvey = () => {
    if (!isEditMode || !editState.valid) {
      return;
    }

    console.log("[SURVEY] = " + JSON.stringify(editState));
    props.startLoading();
    
    const isNewSurvey = editState.survey.id === -1;
    const surveyData = { ...editState.survey };
    
    // If creating a new survey, ensure id is null as backend may expect
    if (isNewSurvey) {
      surveyData.id = null;
    }
    
    axios
      .post(process.env.REACT_APP_API_ENDPOINT + "/surveys/save", surveyData)
      .then(({ data }) => {
        setSelectedRowKey(data.id);
        setIsEditMode(false);
        reloadSurveys();        
        setTimeout(() => { props.stopLoading("success"); }, 1000);        
      })
      .catch(function(error) {
        console.log(error);
        setTimeout(() => { props.stopLoading("failure"); }, 1000);
      });
  };

  const newSurvey = () => {
    // Only change the page if needed
    if (listState.pagination.current !== 1) {
      // First update pagination to page 1 and let the data reload
      setListState({
        ...listState,
        pagination: {
          ...listState.pagination,
          current: 1
        }
      });

      // After the reload is complete, then create the new survey
      setTimeout(() => {
        const newSurvey = { id: -1 };
        setCurrentPageSurveys([newSurvey, ...currentPageSurveys]);
        setEditState({ survey: newSurvey, valid: false });
        setSelectedRowKey(-1);
        setIsEditMode(true);
        setActiveTabKey("1"); // Set active tab to "Info" tab
      }, 500); // Short delay to let the reload complete
    } else {
      // If already on page 1, just create the new survey
      const newSurvey = { id: -1 };
      setCurrentPageSurveys([newSurvey, ...currentPageSurveys]);
      setEditState({ survey: newSurvey, valid: false });
      setSelectedRowKey(-1);
      setIsEditMode(true);
      setActiveTabKey("1"); // Set active tab to "Info" tab
    }
  };

  const cancelEdit = () => {
    // If we're canceling a new survey, remove it from the temporary display
    if (editState.survey.id === -1) {
      setCurrentPageSurveys(currentPageSurveys.filter(survey => survey.id !== -1));
      setSelectedRowKey(null);
      setEditState({ survey: {id:-1, enterpriseId: -1, eventId: -1}, valid: false });
    } else {
      // If we're canceling an edit to an existing survey, remember its ID
      const surveyId = editState.survey.id;
      // We'll reselect this survey after reloading
      setSelectedRowKey(surveyId);
    }

    setIsEditMode(false);
    reloadSurveys();
  };

  const editSurvey = id => {
    const toEdit = currentPageSurveys.find(i => i.id === id);
    // Create a deep copy to avoid reference issues
    const toEditCopy = JSON.parse(JSON.stringify(toEdit));
    setEditState({ survey: toEditCopy, valid: true });
    setIsEditMode(true);
    setSelectedRowKey(id);
  };

  const showSurvey = id => {
    const toShow = currentPageSurveys.find(i => i.id === id);
    setEditState({ survey: toShow, valid: true });
    setIsEditMode(false);
    setSelectedRowKey(id);
  };

  const deleteSurvey = id => {
    if (isEditMode) {
      return; // Prevent deletion while in edit mode
    }
    
    const toDelete = currentPageSurveys.find(i => i.id === id);
    props.startLoading();
    axios
      .delete(process.env.REACT_APP_API_ENDPOINT + "/surveys/delete", {
        params: {
          id: toDelete.id
        }
      })
      .then(({ data }) => {
        reloadSurveys();
        setEditState({ survey: {id:-1, enterpriseId: -1, eventId: -1}, valid: false });
        setSelectedRowKey(null);     
        setTimeout(() => { props.stopLoading("success"); }, 1000);        
      })
      .catch(function(error) {
        console.log(error);
        setTimeout(() => { props.stopLoading("failure"); }, 1000);
      });
  };

  const surveyCange = (surveyData, valid) => {
    var newSurvey = { ...editState};  
    if (surveyData.createdOn && surveyData.createdOn.value) {
      newSurvey.survey.createdOn = surveyData.createdOn.value.format('YYYY-MM-DD');
    }
    if (surveyData.enterprise && surveyData.enterprise.value) {
      newSurvey.survey.enterpriseId = surveyData.enterprise.value;
    }
    if (surveyData.event && surveyData.event.value) {
      newSurvey.survey.eventId = surveyData.event.value;
    }  
    newSurvey.valid = valid;    
    setEditState(newSurvey);
  };

  const reloadSurveys = useCallback(() => {
    // Remember the currently selected survey ID before reloading
    const currentSelectedId = selectedRowKey;
    
    setIsLoading(true);
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/surveys/" + props.type.apiPath, {
        params: {
          // Using the existing API parameters
          pageSize: listState.pagination.pageSize,
          pageNumber: listState.pagination.current,
          sortField: listState.sorter.field,
          sortOrder: listState.sorter.order,
          ...listState.filters
        }
      })
      .then(({ data }) => {
        // Using the existing data structure from API
        setCurrentPageSurveys(data.data);
        setTotalCount(data.totalCount);
        
        // Clear selection if there's no data
        if (data.data.length === 0) {
          setEditState({ survey: {id:-1, enterpriseId: -1, eventId: -1}, valid: false });
          setSelectedRowKey(null);
        }
        // If there was a selected survey, find it in the new data and update editState
        else if (currentSelectedId !== null && currentSelectedId !== -1) {
          const selectedSurvey = data.data.find(survey => survey.id === currentSelectedId);
          if (selectedSurvey) {
            setEditState({ survey: selectedSurvey, valid: true });
            setSelectedRowKey(currentSelectedId);
          }
        }
      })
      .catch(function(error) {
        console.log(error);
      })
      .finally(function() {
        setIsLoading(false);
      });
  }, [listState, props.type, selectedRowKey]);

  const importSurveys = () => {
    notification.open({
      placement: "bottomRight",
      message: 'IMPORT SURVEYS FROM EXTERNAL SYSTEMS',
      description:
        'This feature is currently not available.',
    });
  };

  const handleTabChange = (key) => {
    setActiveTabKey(key);
  };

  useEffect(() => {
    reloadSurveys();
  }, [reloadSurveys]);

  useEffect(() => {
    // Clear selection when type changes
    setEditState({ survey: {id:-1, enterpriseId: -1, eventId: -1}, valid: false });
    setSelectedRowKey(null);
    setIsEditMode(false);
    setCurrentPageSurveys([]);
  }, [props.type.name]);

  const { TabPane } = Tabs;

  // Required fields, surfaced in the Save tooltip when still empty.
  const requiredSurveyFields = [
    { key: 'createdOn', label: 'Date of the survey' },
    { key: 'enterpriseId', label: 'Enterprise' },
    { key: 'eventId', label: 'Event' }
  ];
  const missingSurveyFields = isEditMode
    ? requiredSurveyFields
        .filter(({ key }) => {
          const value = editState.survey?.[key];
          return !value || value === -1;
        })
        .map(({ label }) => label)
    : [];
  const saveDisabledReason = missingSurveyFields.length > 0
    ? `Required fields still empty: ${missingSurveyFields.join(', ')}`
    : '';

  const columns = [
    {
      title: "Date of the survey",
      dataIndex: "createdOn",
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '',
      sorter: true
    },
    {
      title: "Enterprise VAT",
      dataIndex: "enterpriseVat",
      sorter: true
    },
    {
      title: "Enterprise Name",
      dataIndex: "enterpriseName",
      sorter: true
    },
    {
      title: "Event Name",
      dataIndex: "eventName",
      sorter: true
    },
    {
      title: "Event Date",
      dataIndex: "eventDate",
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '',
      sorter: true
    },
    {
      title: "Event Type",
      dataIndex: "eventType", // Change to match the field name that contains the raw type code
      render: (text, record) => {
        if (record.eventTypeDescription) {
          return t(`events.type.${record.eventTypeDescription}`);
        }
        return '';
      },
      sorter: true
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <span>
          <Button
            type="default"
            size="small"
            disabled={isEditMode}
            onClick={(e) => {
              e.stopPropagation();
              editSurvey(record.id);
            }}
          >
            Edit
          </Button>
          <Divider type="vertical" />
          <Button
            type="default"
            size="small"
            disabled={isEditMode}
            onClick={(e) => {
              e.stopPropagation();
              deleteSurvey(record.id);
            }}
          >
            Delete
          </Button>
        </span>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3}>{"Damage & Loss Assessment - " + props.type.name}</Title>
        </Col>
        <Col>
          <Button key="1"
            disabled={isEditMode}
            onClick={() => reloadSurveys()}>
            Refresh
          </Button>
          <Button key="2"
            style={{ marginLeft: 8 }}
            disabled={isEditMode}
            onClick={() => newSurvey()}>
            New
          </Button>
          <Tooltip title={saveDisabledReason}>
            <span style={{ marginLeft: 8 }}>
              <Button
                key="3"
                type="primary"
                disabled={!isEditMode || !editState.valid}
                onClick={() => saveSurvey()}>
                Save
              </Button>
            </span>
          </Tooltip>
          <Button
            key="4"
            style={{ marginLeft: 8 }}
            onClick={() => cancelEdit()}
            disabled={!isEditMode}>
            Cancel
          </Button>
          <Button
            key="5"
            style={{ marginLeft: 8 }}
            disabled={isEditMode}
            onClick={() => importSurveys()}>
            Import
          </Button>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24} style={{ height: 250 }}>
          <Table
            size="small"
            scroll={{ y: 150 }}
            columns={columns}
            rowKey={record => record.id}
            dataSource={currentPageSurveys}
            pagination={{ 
              pageSize: listState.pagination.pageSize,
              current: listState.pagination.current,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} surveys`
            }}
            loading={isLoading}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => {
                if (!isEditMode) {
                  showSurvey(record.id);
                }
              },
              className: record.id === selectedRowKey ? 'selected-row' : '',
              style: isEditMode ? { cursor: 'not-allowed' } : {}
            })}
            rowClassName={(survey) => survey.id === selectedRowKey ? 'selected-row' : ''}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Tabs activeKey={activeTabKey} onChange={handleTabChange}>
            <TabPane
              tab={
                <span>
                  {" "}
                  <AppleOutlined /> Info
                </span>
              }
              key="1"
            >
              <SurveysInfoForm
                record={editState}
                parentCallback={surveyCange}
                type={props.type}
                isEditMode={isEditMode}
              />
            </TabPane>
            <TabPane
              tab={
                <span>
                  <AndroidOutlined />
                  Damages
                </span>
              }
              key="2"
              disabled={!editState.survey || editState.survey.id === -1}
            >
              <SurveysDamageInfo 
                record={editState} 
                type={props.type}
                isEditMode={isEditMode}
              />
            </TabPane>
          </Tabs>
        </Col>
      </Row>
    </div>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    startLoading: () => dispatch({ type: "START_LOADING" }),
    stopLoading: val => dispatch({ type: "STOP_LOADING", status: val })
  };
};

export default connect(null, mapDispatchToProps)(Surveys);